import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { format } from 'date-fns';
import { OperatorFunction, pipe } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { Mi2DataResult } from '../card--my-coaching-card/models/mi2-data-result';
import { MyCoachingCard } from '../card--my-coaching-card/models/my-coaching-card.model';
import { StagingKpiThreshold } from '../card--my-coaching-card/models/staging-kpi-threshold.model';
import { WpCoachingCard } from '../card--my-coaching-card/models/wp-coaching-card.model';
import { PerformanceCard } from '../card--my-performance-card/models/performance-card.model';
import { RepCard } from '../card-coaching-card-rep/models/rep-card.model';
import {
    CoachingInsightComponentAttributes,
    CoachingSummaryResponse,
    transformAttributesToComponentAttributes,
} from '../coaching-insight/oa/models/coaching.global.model';
import { APIResponse } from '../common/common.model';
import { catchErrorLogRet, mapRetrieve } from '../common/rest.operators';
@Injectable({ providedIn: 'root' })
export class InsightService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    retrieveMyCoachingCard<T>(
        project: () => {
            userId: number;
            periodTypeId: number;
            effectiveDate?: Date;
            startDate?: Date;
            endDate?: Date;
        },
    ): OperatorFunction<T, MyCoachingCard> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.userId != null) {
                    params = params.set('userId', args.userId);
                }
                if (args.periodTypeId != null) {
                    params = params.set('periodTypeId', args.periodTypeId);
                }
                if (args.effectiveDate != null) {
                    params = params.set(
                        'effectiveDate',
                        format(args.effectiveDate, 'yyyy-MM-dd'),
                    );
                }
                if (args.startDate != null) {
                    params = params.set(
                        'startDate',
                        format(args.startDate, 'yyyy-MM-dd'),
                    );
                }
                if (args.endDate != null) {
                    params = params.set(
                        'endDate',
                        format(args.endDate, 'yyyy-MM-dd'),
                    );
                }
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/card`,
                    { params },
                );
            }),
            // mapRetrieve(new CoachingCardTransformer()),
            catchErrorLogRet(null),
        );
    }

    retrieveRepCard<T>(
        project: () => {
            userId: number;
            periodTypeId: number;
        },
    ): OperatorFunction<T, RepCard> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.userId != null) {
                    params = params.set('userId', args.userId);
                }
                if (args.periodTypeId != null) {
                    params = params.set('periodTypeId', args.periodTypeId);
                }

                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/repcard`,
                    { params },
                );
            }),
            catchErrorLogRet(null),
        );
    }

    retrievePerformanceCard<T>(
        project: () => {
            userId: number;
            periodTypeId?: number;
            eventDate?: Date;
            definitionIdentifier?: string;
            cardType?: string;
        },
    ): OperatorFunction<T, PerformanceCard> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.userId != null) {
                    params = params.set('userId', args.userId);
                }
                if (args.periodTypeId != null) {
                    params = params.set('periodTypeId', args.periodTypeId);
                }
                if (args.eventDate != null) {
                    params = params.set(
                        'eventDate',
                        format(args.eventDate, 'yyyy-MM-dd'),
                    );
                }
                if (args.definitionIdentifier != null) {
                    params = params.set(
                        'definitionIdentifier',
                        args.definitionIdentifier,
                    );
                }
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.INSIGHT}/card/${
                        args.cardType ?? ''
                    }`,
                    { params },
                );
            }),
            catchErrorLogRet(null),
        );
    }

    retrieveMyStagingKpiThresholds<T>(
        project: () => {},
    ): OperatorFunction<T, StagingKpiThreshold[]> {
        return pipe(
            switchMap(() => {
                return this._http.get(
                    `${this._appConfig.API_ENDPOINTS.COACHING}/card-meta/kpi-thresholds`,
                );
            }),
            catchErrorLogRet(null),
        );
    }

    retrieveMi2Data<T>(
        project: () => {
            userId: number;
            periodTypeId: number;
            isFlMi2: boolean;
        },
    ): OperatorFunction<T, Mi2DataResult> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.userId != null) {
                    params = params.set('userId', args.userId);
                }
                if (args.periodTypeId != null) {
                    params = params.set('periodTypeId', args.periodTypeId);
                }
                if (args.isFlMi2 != null) {
                    params = params.set('isFlMi2', args.isFlMi2);
                }
                return this._http
                    .get(
                        `${this._appConfig.API_ENDPOINTS.SCORECARD}/mi2/mi2-data`,
                        { params },
                    )
                    .pipe(mapRetrieve(), catchErrorLogRet({}));
            }),
        );
    }

    retrieveWpCoachingCard<T>(
        project: () => {
            userId: number;
        },
    ): OperatorFunction<T, WpCoachingCard> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.userId != null) {
                    params = params.set('userId', args.userId);
                }
                return this._http
                    .get<WpCoachingCard>(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/winning-percentage/wp-card`,
                        { params },
                    )
                    .pipe(catchErrorLogRet({}));
            }),
        );
    }

    retrieveCoachingComponents<T>(
        project: () => {
            performerUserId: number;
            periodTypeId: number;
        },
    ): OperatorFunction<T, CoachingInsightComponentAttributes> {
        return pipe(
            switchMap(() => {
                const args = project();
                let params = new HttpParams();
                if (args.performerUserId != null) {
                    params = params.set(
                        'performerUserId',
                        args.performerUserId,
                    );
                }
                if (args.periodTypeId != null) {
                    params = params.set('periodTypeId', args.periodTypeId);
                }
                return this._http
                    .get<APIResponse<CoachingSummaryResponse>>(
                        `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/summary`,
                        { params },
                    )
                    .pipe(
                        mapRetrieve(),
                        catchErrorLogRet({}),
                        map((response) => {
                            return transformAttributesToComponentAttributes(
                                response.attributes,
                            );
                        }),
                    );
            }),
        );
    }
}
